import { Schema, SchemaGetter } from "effect"
import { AllowRule } from "./allow"
import { AgentKind, Effort, ModelsByAgent, Taint, tolerantLiterals } from "./common"
import { LoadedDocument } from "./documents"
import { MemoryScope, MemoryType } from "./domain/knowledge"
import {
  AgentId,
  AreaId,
  ArtifactId,
  Epoch,
  GoalId,
  ItemId,
  MemoryItemId,
  Millis,
  NonNegInt,
  PosInt,
  ProfileId,
  ProjectId,
  RepoId,
  RunId,
  RunnerId,
  Sha256,
  ShortRef,
  UserId,
  WorkerRef,
  WorkspaceId,
} from "./ids"
import { RoutingRule } from "./routing"
import { Handoff, RunKind } from "./run"
import { AccessRequest, JobAccess } from "./runner"

// #region context-pack
const LoadedItemReason = Schema.TaggedUnion({
  always: {},
  trigger: { matched: Schema.String },
  summary: {},
  on_demand: {},
})

/**
 * One line of the "why did it do that?" manifest. Revoking a bad item shows every run that loaded it.
 *
 * Runners carry the manifest in `turn.start` but never act on it, so its enums are forward compatible: a memory type
 * or load reason a newer cloud adds decodes as `fact` / `on_demand` instead of failing the frame (see
 * `tolerantLiterals`). The cloud only writes known values.
 */
export const LoadedItem = Schema.Struct({
  memoryItemId: MemoryItemId,
  revision: NonNegInt,
  scope: MemoryScope,
  type: tolerantLiterals(MemoryType.literals, "fact"),
  reason: Schema.Union([
    LoadedItemReason,
    Schema.Struct({ _tag: Schema.String }).pipe(
      Schema.decodeTo(Schema.toType(LoadedItemReason), {
        decode: SchemaGetter.transform((): typeof LoadedItemReason.Type => ({ _tag: "on_demand" })),
        encode: SchemaGetter.forbidden(() => "an unknown load reason is never encoded"),
      }),
    ),
  ]),
  tokens: NonNegInt,
  tainted: Schema.Boolean,
})

/** The only relative paths a pack may write. The runner rejects anything else (no arbitrary paths, no ".."). */
export const PackPath = Schema.String.check(
  Schema.isPattern(
    /^(AGENTS\.md|CLAUDE\.md|\.memory\/[A-Za-z0-9._/-]+|\.claude\/skills\/[A-Za-z0-9._/-]+|\.agents\/skills\/[A-Za-z0-9._/-]+)$/,
  ),
  Schema.makeFilter((s: string) => !s.includes("..") || "no .. segments"),
)

export const PackFile = Schema.Struct({
  path: PackPath,
  sha256: Sha256,
  bytes: NonNegInt,
  /** Inline below 64 KiB, else fetched from R2 with the run's stream token. */
  content: Schema.TaggedUnion({ inline: { text: Schema.String }, blob: { key: Schema.String } }),
})

/**
 * Decision 0050: a file the runner downloads into the pack dir before the run starts (informs inputs and the item's
 * own outputs; chat files of a turn). NOT part of `ContextPack.hash` (an older runner would fail the hash check);
 * each file is verified by its own sha256. Its own path pattern: only a flat name under `.memory/attachments/`, so an
 * attachment can never overwrite AGENTS.md, CLAUDE.md, a skill or another hash-verified pack file.
 */
export const AttachmentPath = Schema.String.check(
  Schema.isPattern(/^\.memory\/attachments\/[A-Za-z0-9._-]+$/),
)
export const PackAttachment = Schema.Struct({
  path: AttachmentPath,
  sha256: Sha256,
  bytes: NonNegInt,
  contentType: Schema.String,
  artifactId: Schema.NullOr(ArtifactId), // null for a chat file
})
export type PackAttachment = typeof PackAttachment.Type

/**
 * Decision 0150: an artifact that reaches an item's run as an input (through `informs`, or the item's own earlier
 * output). A runner with `inputs_materialized` places it at `.minga/inputs/<ref>/<name>` in the run's working
 * directory and lists it in the run's prompt; an older runner ignores the key. Outside `ContextPack.hash`, verified by
 * its own sha256. Loose strings on purpose: the runner checks `ref` and `name` (one flat segment each) itself, so a
 * value it cannot use skips that input, never the pack. `skip` set by the cloud = not downloaded (too big), and why.
 */
export const PackInput = Schema.Struct({
  ref: Schema.String,
  name: Schema.String,
  title: Schema.String,
  sha256: Schema.String,
  bytes: Schema.Number,
  contentType: Schema.String,
  artifactId: Schema.String,
  tainted: Schema.Boolean,
  /** `informs` (handed on by another item) or `own` (this item's earlier output). */
  source: Schema.String,
  skip: Schema.optionalKey(Schema.String),
})
export type PackInput = typeof PackInput.Type
/** Decision 0150: the directory inputs are placed in, relative to the run's working directory. */
export const INPUTS_DIR = ".minga/inputs"
/** Decision 0150: at most this many inputs and bytes are placed; the rest are listed as skipped. */
export const INPUTS_MAX_FILES = 25
export const INPUTS_MAX_BYTES = 100 * 1024 * 1024

/**
 * Assembled in the cloud by core.assembleContext when the run is requested; stored content-addressed in R2.
 * Its hash is signed into the job; the sink streams it in turn.start; the runner verifies before writing.
 */
export const ContextPack = Schema.Struct({
  hash: Sha256, // sha256 over the canonical list of (path, sha256)
  files: Schema.Array(PackFile),
  /** Decision 0050: files fetched into `.memory/attachments/` (outside `hash`; runners before 0.6.0 ignore them). */
  attachments: Schema.optionalKey(Schema.Array(PackAttachment)),
  /** Decision 0150: inputs placed into the working directory (outside `hash`; runners before 0.9.24 ignore them). */
  inputs: Schema.optionalKey(Schema.Array(PackInput)),
  manifest: Schema.Array(LoadedItem),
  documents: Schema.optionalKey(Schema.Array(LoadedDocument)),
  /** Every run re-reads the original goal and the specs above it in full, never only a parent's summary. */
  ancestry: Schema.Struct({
    goalIds: Schema.Array(GoalId),
    /** The top-level item above the run's item (was `initiativeId`). */
    topItemId: Schema.NullOr(ItemId),
    areaId: Schema.NullOr(AreaId),
    itemId: Schema.NullOr(ItemId),
  }),
  /** The description revisions the pack holds: the item's and every ancestor's whose description was included. */
  specRevs: Schema.optionalKey(Schema.Array(Schema.Struct({ itemId: ItemId, rev: PosInt }))),
  handoff: Schema.NullOr(Handoff),
  taint: Taint, // the most tainted thing loaded; decides which grants the run gets (Rule of Two), checked per call
  tokenEstimate: NonNegInt,
})
// #endregion context-pack

// #region job
export const ToolClass = Schema.Literals(["graph", "read", "write", "outward"])

/** One MCP server for the run. The url is always Minga's (Project DO graph endpoint or tool gateway). */
export const McpGrant = Schema.Struct({
  name: Schema.String, // "minga", "openseo"
  url: Schema.String,
  token: Schema.Redacted(Schema.String), // in the signed job and Renew responses only; never in a pack or transcript
  expiresAt: Millis, // ≤ lease.expiresAt + 10 s; refreshed by Renew
  tools: Schema.Array(Schema.Struct({ name: Schema.String, class: ToolClass, schemaHash: Sha256 })),
})

/**
 * HMAC-signed by the cloud (it issues and verifies). Identity only: grants, pins and taint are NOT in the token.
 * The endpoint checks them server-side on EVERY call (with epoch current and run not ended), so revoking a grant
 * or raising taint applies immediately, mid-run.
 */
export const McpTokenClaims = Schema.Struct({
  jti: Schema.String,
  aud: Schema.Literals(["graph", "gateway"]),
  sub: RunId,
  epoch: Epoch,
  /** D7: the wire name `taskId` stays this release; it holds the item id. */
  actor: Schema.TaggedUnion({ agent: { agentId: AgentId }, worker: { taskId: ItemId, agentId: AgentId } }),
  principal: UserId, // whose grants are used; never a different member's
  projectId: Schema.NullOr(ProjectId),
  areaId: Schema.NullOr(AreaId),
  exp: Millis, // ≤ lease.expiresAt + 10 s
})

export const PermissionProfile = Schema.Literals([
  "read_only",
  "workspace_write",
  "workspace_write_network",
  "full_auto",
])

export const JobWorkspace = Schema.TaggedUnion({
  none: {}, // turns that only use graph tools
  worktree: {
    repoId: RepoId,
    /** Branch name (resolved as origin/<name> first) or a 40-hex sha. */
    baseRef: Schema.String,
    /** ^minga/[a-z0-9][a-z0-9._/-]{0,100}$, checked by the runner. */
    branch: Schema.String,
    /** D7: holds an item id. */
    reuseOf: Schema.NullOr(ItemId),
    /**
     * The worktree path key <root>/<repo>/<worktreeKey> (A4). Workers: the item id. Validators:
     * `<itemId>-review-<round>`, so a review never lands in the worker's worktree. Absent (older templates) = taskId.
     */
    worktreeKey: Schema.optionalKey(Schema.String.check(Schema.isPattern(/^[A-Za-z0-9_-]{1,80}$/))),
    /** Offer the runner-local tool `open_pull_request` (W9). Draft PRs only this wave. */
    pullRequest: Schema.optionalKey(Schema.Literal("draft")),
  },
  scratch: {}, // empty temp dir (content work)
})

/**
 * Optional run caps. Each null = no Minga cap: the runner passes nothing and the CLI's own default applies (rev 2.1,
 * decision 0018: no turn or time limits at first). A set `wallClockMs` excludes time in awaiting_permission (W22).
 */
export const RunLimits = Schema.Struct({
  maxTurns: Schema.NullOr(PosInt),
  wallClockMs: Schema.NullOr(PosInt),
  /**
   * Wave 3 (lessons 1–4): the runner interrupts a run whose CLI produced nothing for this long (not while it waits on
   * a permission or on its own background tasks) and ends it `no_progress`. The hub sets it only for a runner whose
   * manifest lists the feature `no_progress` (an older runner could not report that stop); absent = no watchdog.
   */
  idleMs: Schema.optionalKey(PosInt),
})

/**
 * Decision 0143: the id one person's request is followed by across the web, the Worker, the DOs and the runner
 * (`msg:<messageId>`, `cmd:<commandId>`, `run:<runId>`). Ids only; carried for logs and telemetry, never acted on.
 */
export const CorrelationId = Schema.String.check(Schema.isPattern(/^[A-Za-z0-9_.:@-]{1,160}$/))

/** The only thing a runner executes. No shell verb, no absolute path, no environment variable, no home path. */
export const JobSpec = Schema.Struct({
  v: Schema.Literal(1),
  runId: RunId,
  epoch: Epoch,
  kind: RunKind,
  principal: UserId,
  projectId: Schema.NullOr(ProjectId),
  /**
   * D7 (decision 0039): the wire name stays this release; it holds the ITEM id (workers, validators):
   * RunnerRepo.worktrees and SessionRef.projectKey. The path key is worktreeKey.
   */
  taskId: Schema.optionalKey(ItemId),
  /** The agent (turns): the turn cwd <data>/agents/<agentId>/. */
  agentId: Schema.optionalKey(AgentId),
  /** "R-12", for logs and `minga status`. */
  runRef: Schema.optionalKey(ShortRef),
  agent: Schema.Struct({
    kind: AgentKind,
    profileId: ProfileId,
    model: Schema.NullOr(Schema.String),
    effort: Schema.NullOr(Effort),
  }),
  workspace: JobWorkspace,
  contextPack: Schema.Struct({ hash: Sha256, bytes: NonNegInt }),
  mcp: Schema.Array(McpGrant),
  permission: PermissionProfile, // limited by the runner's ceiling
  /** Authoritative on the runner (over TurnStart.settings.maxTurns), W22. null = no Minga cap (rev 2.1, the default
   * this wave): the runner passes nothing and the CLI's own default applies. A set wall clock excludes awaiting_permission. */
  limits: RunLimits,
  /** Where the runner dials the run stream: always the run's Run DO (W2), which relays turns to the Agent DO. */
  stream: Schema.Struct({ url: Schema.String, token: Schema.Redacted(Schema.String) }),
  /** publish runs on a runner: exactly the approved call (no model). */
  publish: Schema.NullOr(
    Schema.Struct({
      integration: Schema.String,
      tool: Schema.String,
      argsHash: Sha256,
      toolSchemaHash: Sha256,
    }),
  ),
  notAfter: Millis, // must start before this
  /** Settings wave (decision 0026): narrows the profile for this run; present only when it narrows something. A
   *  runner offers itself for such a job only with the manifest feature `access_request` (core K14). Decision 0096:
   *  `access.allow` carries the effective sandbox allowlist, only to a runner with the feature `access_allow`. */
  access: Schema.optionalKey(JobAccess),
  /** The temporary teammate ("W-3"), for logs and `minga status`. Display only. */
  workerRef: Schema.optionalKey(WorkerRef),
  /**
   * Decision 0141: where the runner fetches this run's secrets (`GET`, `Authorization: Bearer <stream token>`), and their
   * names. Only to a runner with the feature `secrets`; values are never in the job.
   */
  secrets: Schema.optionalKey(Schema.Struct({ url: Schema.String, names: Schema.Array(Schema.String) })),
  /**
   * Decision 0143: the run's correlation id, for the runner's logs and telemetry reports only. Optional and additive:
   * an older runner drops the unknown key (Struct decoding ignores excess keys).
   */
  correlationId: Schema.optionalKey(CorrelationId),
})

/**
 * Detached Ed25519 signature by a separate signer; the runner pins its public keys at pairing.
 * `payload` = canonicalJson(Schema.encodeSync(Schema.toCodecJson(JobSpec))(spec)) — the JSON codec, so Redacted
 * tokens are their values (a plain encodeSync keeps Redacted objects). The runner verifies `payload` as received,
 * then decodes it with Schema.toCodecJson(JobSpec). `sig` = base64url Ed25519 signature over its UTF-8 bytes.
 */
export const SignedJob = Schema.Struct({ kid: Schema.String, payload: Schema.String, sig: Schema.String })

/** HMAC-signed by the cloud like McpTokenClaims; authorises one runner to dial one run's stream. */
export const StreamTokenClaims = Schema.Struct({
  jti: Schema.String,
  aud: Schema.Literal("stream"),
  sub: RunId,
  epoch: Epoch,
  runnerId: RunnerId,
  workspaceId: WorkspaceId,
  projectId: Schema.NullOr(ProjectId),
  /** With runnerId, decides whether a SessionRef is resumable on this executor (A3). */
  profileId: ProfileId,
  exp: Millis, // lease.expiresAt + 10 s; re-minted by every Renew (W8), like the MCP token
})

/**
 * The run-specific half of a JobSpec, frozen by the Project DO with the run and carried in Enqueue. At Claim the hub
 * adds runId, epoch, agent.kind/profileId (from the offered profile), mcp, stream and notAfter, then signs. A null
 * taskId/agentId is omitted from the JobSpec (optional keys there). D7: `taskId` holds the item id.
 */
export const JobTemplate = Schema.Struct({
  v: Schema.Literal(1),
  kind: RunKind,
  principal: UserId,
  projectId: Schema.NullOr(ProjectId),
  areaId: Schema.NullOr(AreaId),
  taskId: Schema.NullOr(ItemId),
  agentId: Schema.NullOr(AgentId),
  runRef: ShortRef,
  /** The MCP grant's actor: turns act as the agent, workers/validators as the worker. */
  actor: McpTokenClaims.fields.actor,
  model: Schema.NullOr(Schema.String),
  effort: Schema.NullOr(Effort),
  workspace: JobWorkspace,
  contextPack: Schema.Struct({ hash: Sha256, bytes: NonNegInt }),
  permission: PermissionProfile,
  /** Authoritative on the runner (W22); null = no Minga cap (rev 2.1, the default this wave). */
  limits: RunLimits,
  publish: JobSpec.fields.publish, // always null this wave
  /** Settings wave: the run's narrowing request (`Run.access`), only when it narrows something. */
  access: Schema.optionalKey(AccessRequest),
  /** Decision 0096: the project's allowlist rules when the run was enqueued; the hub adds the workspace's at Claim. */
  allow: Schema.optionalKey(Schema.Array(AllowRule)),
  /** Workers/validators: model and effort per CLI family; the hub picks the entry at Claim when the lease decides
   *  the family (`model ?? byAgent[kind].model`, the same for effort). */
  byAgent: Schema.optionalKey(ModelsByAgent),
  routing: Schema.optionalKey(RoutingRule),
  /** The temporary teammate (`Run.worker.ref`). Display only. */
  workerRef: Schema.optionalKey(WorkerRef),
  /** Decision 0143: the run's correlation id (its RunRequested event's), copied into the JobSpec at Claim. */
  correlationId: Schema.optionalKey(CorrelationId),
})
export type JobTemplate = typeof JobTemplate.Type
// #endregion job
